import * as THREE from "three";
import { COLOR_HEX, type Color } from "@core/colors";
import { makeRoundedRect, setupOrtho, tween } from "@core/three-helpers";
import { loadLevels, getLevel, nextLevelIndex, levelNumber } from "@core/level-loader";
import { levels as levelData } from "./levels";

const canvas = document.getElementById("game") as HTMLCanvasElement;
const hudLevel = document.getElementById("hud-level")!;
const hudStatus = document.getElementById("hud-status")!;
const banner = document.getElementById("banner")!;

const { scene, camera, renderer, worldFromEvent } = setupOrtho(canvas, {
  virtualHeight: 22,
  background: 0x182231,
});

const BOX_CAPACITY = 3;

interface Hole {
  id: number;
  x: number;
  y: number;
  pin: Pin | null;
  marker: THREE.Mesh;
}

interface Pin {
  id: number;
  mesh: THREE.Group;
  hole: Hole;
}

interface Band {
  id: number;
  color: Color;
  holes: Hole[]; // holes this band is anchored to
  line: THREE.Line;
  released: boolean;
}

interface BoxState {
  color: Color;
  group: THREE.Group;
  fillMesh: THREE.Mesh;
  pieces: number;
}

let holes: Hole[] = [];
let pins: Pin[] = [];
let bands: Band[] = [];
let boxes: BoxState[] = [];
let nextId = 1;
let currentLevelIdx = 0;
const allLevels = loadLevels(levelData);
let busy = false;

const FIELD_TOP = 8;
const FIELD_BOTTOM = -2;
const BOXES_Y = -7;
const HOLE_RADIUS = 0.42;

function makeHole(x: number, y: number): Hole {
  const id = nextId++;
  const geom = new THREE.RingGeometry(HOLE_RADIUS, HOLE_RADIUS + 0.08, 24);
  const mat = new THREE.MeshBasicMaterial({ color: 0x6481a0, side: THREE.DoubleSide });
  const marker = new THREE.Mesh(geom, mat);
  marker.position.set(x, y, 0.02);
  scene.add(marker);
  return { id, x, y, pin: null, marker };
}

function makePin(hole: Hole): Pin {
  const id = nextId++;
  const group = new THREE.Group();
  group.position.set(hole.x, hole.y, 0.5);
  const head = new THREE.Mesh(
    new THREE.CircleGeometry(HOLE_RADIUS * 0.85, 24),
    new THREE.MeshBasicMaterial({ color: 0xece6ff }),
  );
  head.position.z = 0.05;
  group.add(head);
  const dot = new THREE.Mesh(
    new THREE.CircleGeometry(HOLE_RADIUS * 0.28, 24),
    new THREE.MeshBasicMaterial({ color: 0x4a6890 }),
  );
  dot.position.z = 0.1;
  group.add(dot);
  scene.add(group);
  const pin: Pin = { id, mesh: group, hole };
  hole.pin = pin;
  return pin;
}

function makeBand(color: Color, holesList: Hole[]): Band {
  const id = nextId++;
  const positions = holesList.flatMap((h) => [h.x, h.y, 0.3]);
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  const mat = new THREE.LineBasicMaterial({ color: COLOR_HEX[color], linewidth: 6 });
  const line = new THREE.Line(geom, mat);
  scene.add(line);
  return { id, color, holes: [...holesList], line, released: false };
}

function refreshBandLine(b: Band) {
  const arr = b.holes
    .filter((h) => h.pin !== null)
    .flatMap((h) => [h.x, h.y, 0.3]);
  if (arr.length === 0) {
    b.line.visible = false;
    return;
  }
  if (arr.length < 6) {
    // Single anchor — show small loop
    const h = b.holes.find((hh) => hh.pin !== null)!;
    arr.push(h.x + 0.05, h.y + 0.05, 0.3);
  }
  b.line.geometry.setAttribute("position", new THREE.Float32BufferAttribute(arr, 3));
  b.line.geometry.attributes.position.needsUpdate = true;
  b.line.geometry.computeBoundingSphere();
}

function makeBoxes(palette: Color[]) {
  const w = 1.7;
  const gap = 0.3;
  const total = palette.length * w + (palette.length - 1) * gap;
  const startX = -total / 2 + w / 2;
  for (let i = 0; i < palette.length; i++) {
    const color = palette[i];
    const group = new THREE.Group();
    group.position.set(startX + i * (w + gap), BOXES_Y, 0);

    const back = makeRoundedRect(w, w, 0.25, 0x2a3a4f);
    group.add(back);
    const rim = makeRoundedRect(w * 0.92, w * 0.3, 0.12, COLOR_HEX[color]);
    rim.position.set(0, w * 0.3, 0.1);
    group.add(rim);
    const fill = makeRoundedRect(w * 0.84, 0.02, 0.05, COLOR_HEX[color]);
    fill.position.set(0, -w * 0.36, 0.15);
    fill.scale.y = 0.001;
    group.add(fill);

    scene.add(group);
    boxes.push({ color, group, fillMesh: fill, pieces: 0 });
  }
}

function clearScene() {
  for (const h of holes) scene.remove(h.marker);
  for (const p of pins) scene.remove(p.mesh);
  for (const b of bands) scene.remove(b.line);
  for (const x of boxes) scene.remove(x.group);
  holes = [];
  pins = [];
  bands = [];
  boxes = [];
}

function spawnLevel(levelIdx: number) {
  clearScene();
  const cfg = getLevel(allLevels, levelIdx);

  // Adapt step size so larger grids still fit the field.
  const cols = cfg.cols;
  const rows = cfg.rows;
  const stepX = cols <= 3 ? 2.2 : Math.max(1.6, 6.6 / (cols - 1));
  const stepY = rows <= 3 ? 2.2 : Math.max(1.6, 6.6 / (rows - 1));
  const startX = -((cols - 1) * stepX) / 2;
  const startY = FIELD_TOP - 2;

  const grid: Hole[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: Hole[] = [];
    for (let c = 0; c < cols; c++) {
      const h = makeHole(startX + c * stepX, startY - r * stepY);
      holes.push(h);
      row.push(h);
    }
    grid.push(row);
  }

  // Free-hole set from config (coords).
  const freeKey = (c: number, r: number) => `${c},${r}`;
  const freeSet = new Set(cfg.freeHoles.map((f) => freeKey(f.col, f.row)));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (freeSet.has(freeKey(c, r))) continue;
      pins.push(makePin(grid[r][c]));
    }
  }

  // Spawn bands from level config (resolve hole coords → Hole refs).
  for (const spec of cfg.bands) {
    const resolved: Hole[] = spec.holes.map((co) => grid[co.row][co.col]);
    const b = makeBand(spec.color, resolved);
    bands.push(b);
    refreshBandLine(b);
  }

  // Boxes — one per unique band color, in stable order of first appearance.
  const palette: Color[] = [];
  for (const spec of cfg.bands) {
    if (!palette.includes(spec.color)) palette.push(spec.color);
  }
  makeBoxes(palette);

  hudLevel.textContent = `Level ${levelNumber(levelIdx)}`;
  hudStatus.textContent = "Tap a pin to move it into the free hole";
  busy = false;
  // Note: y bounds are also derived from FIELD_BOTTOM (see future use)
  void FIELD_BOTTOM;
}

function showBanner(text: string, durationMs: number) {
  banner.textContent = text;
  banner.classList.add("show");
  setTimeout(() => banner.classList.remove("show"), durationMs);
}

function findFreeHole(): Hole | null {
  return holes.find((h) => h.pin === null) ?? null;
}

function onPointerDown(ev: PointerEvent) {
  if (busy) return;
  const world = worldFromEvent(ev);
  // Find a pin under the touch
  let bestPin: Pin | null = null;
  let bestDist = HOLE_RADIUS + 0.2;
  for (const p of pins) {
    const dx = world.x - p.mesh.position.x;
    const dy = world.y - p.mesh.position.y;
    const d = Math.hypot(dx, dy);
    if (d < bestDist) {
      bestDist = d;
      bestPin = p;
    }
  }
  if (!bestPin) return;
  const free = findFreeHole();
  if (!free) return;
  movePin(bestPin, free);
}

function movePin(pin: Pin, target: Hole) {
  busy = true;
  const from = pin.hole;
  // Swap occupancy
  from.pin = null;
  target.pin = pin;
  pin.hole = target;
  tween(pin.mesh, { x: target.x, y: target.y }, 280, () => {
    busy = false;
    for (const b of bands) refreshBandLine(b);
    checkReleasedBands();
  });
}

function checkReleasedBands() {
  for (const b of bands) {
    if (b.released) continue;
    const anchored = b.holes.some((h) => h.pin !== null);
    if (!anchored) releaseBand(b);
  }
}

function releaseBand(b: Band) {
  b.released = true;
  // Find midpoint of original holes
  const x = b.holes.reduce((s, h) => s + h.x, 0) / b.holes.length;
  const y = b.holes.reduce((s, h) => s + h.y, 0) / b.holes.length;
  // Move line geometry visual to a small drop (single dot)
  b.line.geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute([x - 0.1, y, 0.3, x + 0.1, y, 0.3], 3),
  );
  b.line.geometry.attributes.position.needsUpdate = true;
  // Animate falling
  const target = boxes.find((bx) => bx.color === b.color && bx.pieces < BOX_CAPACITY);
  if (!target) {
    hudStatus.textContent = "No box for this color — restart";
    return;
  }
  // Use line.position to slide it down (animate the line as a whole)
  // Since lines don't track position uniformly, recompute geometry over time.
  animateBandToBox(b, x, y, target);
}

function animateBandToBox(b: Band, x: number, y: number, target: BoxState) {
  const tx = target.group.position.x;
  const ty = target.group.position.y;
  const start = performance.now();
  const dur = 540;
  function step(now: number) {
    const t = Math.min(1, (now - start) / dur);
    const e = 1 - Math.pow(1 - t, 3);
    const cx = x + (tx - x) * e;
    const cy = y + (ty - y) * e;
    b.line.geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute([cx - 0.4, cy, 0.3, cx + 0.4, cy, 0.3], 3),
    );
    b.line.geometry.attributes.position.needsUpdate = true;
    if (t < 1) requestAnimationFrame(step);
    else {
      scene.remove(b.line);
      target.pieces++;
      target.fillMesh.scale.y = Math.max(0.001, (target.pieces / BOX_CAPACITY) * 28);
      if (target.pieces >= BOX_CAPACITY) flyAwayBox(target);
      maybeWin();
    }
  }
  requestAnimationFrame(step);
}

function flyAwayBox(b: BoxState) {
  tween(b.group, { y: b.group.position.y + 6, sx: 0.2, sy: 0.2 }, 460, () => {
    scene.remove(b.group);
  });
}

function maybeWin() {
  if (bands.every((b) => b.released)) {
    showBanner(`Level ${levelNumber(currentLevelIdx)} clear!`, 1700);
    busy = true;
    setTimeout(() => {
      currentLevelIdx = nextLevelIndex(allLevels, currentLevelIdx);
      spawnLevel(currentLevelIdx);
    }, 1700);
  }
}

canvas.addEventListener("pointerdown", onPointerDown);
spawnLevel(currentLevelIdx);

function loop() {
  requestAnimationFrame(loop);
  renderer.render(scene, camera);
}
loop();
